import React, { useEffect } from 'react';
import { useRecoilState } from 'recoil';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { Heading5 } from '~/components/atom/Typography';
import TextButton from '~/components/atom/TextButton';
import Textarea from '~/components/molecule/Textarea';
import { type Lfa_Service_Feedback } from '~/graphql/types';
import AddServiceBlock from './components/AddServiceBlock';
import SnoozeBlock from './components/SnoozeBlock';
import servicesFeedbackState from '~/components/page/LFA/state/servicesFeedbackState';
import useServiceFeedback from './hooks/useServiceFeedback';
import Services from './components/Services';
import type {
  ClientLfaConversion,
  ClientLfaConversionData,
} from '~/components/page/LFA/types';

const text = {
  questions:
    'Wat is de uitkomst van deze afspraak? Wij hebben al een selectie voor je gemaakt (kies ten minste één optie)',
  additionalServices: 'Andere diensten zijn geleverd',
  additionalComments: 'Aanvullende opmerkingen (optioneel)',
  additionalCommentsPlaceholder: 'Laat feedback achter...',
};

type Props = {
  selectedServices: ClientLfaConversion['selectedServices'];
  lastServiceFeedback?: Array<Lfa_Service_Feedback> | null;
  enabledServices: ClientLfaConversionData['enabledServices'];
};

const AppointmentHappened: React.FC<Props> = ({
  selectedServices: initialSelectedServices,
  lastServiceFeedback,
  enabledServices,
}) => {
  const [, setServicesFeedback] = useRecoilState(servicesFeedbackState);
  const {
    state,
    additionalServices,
    errorsToShowLookup,
    clearValidationErrorForField,
    buildServicesFeedback,
    handleUpdateFeedback,
    handleRemoveService,
    hasAdditionalServices,
    handleToggleAdditionalServices,
    handleAddService,
    handleSnoozeUntilChange,
    handleSnoozeNoteChange,
    handleAdditionalCommentsChange,
    hasSnoozedService,
  } = useServiceFeedback({
    initialSelectedServices,
    lastServiceFeedback,
    enabledServices,
  });

  useEffect(() => {
    if (state.initialized) {
      const feedbackData = buildServicesFeedback();

      if (feedbackData) {
        setServicesFeedback(feedbackData);
      }
    }
  }, [buildServicesFeedback, setServicesFeedback, state.initialized]);

  return (
    <JustificationContainer direction="column" gap="s">
      <Heading5 margin={[null]}>{text.questions}</Heading5>

      <Services
        handleUpdateFeedback={handleUpdateFeedback}
        handleRemoveService={handleRemoveService}
        errorsToShowLookup={errorsToShowLookup}
        initialSelectedServices={initialSelectedServices}
        selectedServices={state.selectedServices}
        serviceFeedback={state.serviceFeedback}
        clearValidationErrorForField={clearValidationErrorForField}
      />

      {hasAdditionalServices && (
        <TextButton
          size="medium"
          label={text.additionalServices}
          icon={state.showAdditionalServices ? 'chevron' : 'chevron-right'}
          direction="rtl"
          padding={[null]}
          underline
          onClick={handleToggleAdditionalServices}
        />
      )}

      {state.showAdditionalServices && hasAdditionalServices && (
        <JustificationContainer direction="column" gap="s" width="100%">
          {additionalServices.map(service => (
            <AddServiceBlock
              key={service.id}
              label={service.name}
              onAddService={() => handleAddService(service)}
            />
          ))}
        </JustificationContainer>
      )}

      {hasSnoozedService && (
        <SnoozeBlock
          error={
            errorsToShowLookup['snoozeDate']?.[0] ||
            errorsToShowLookup['snoozeNote']?.[0]
          }
          snoozeUntil={state.snoozeUntil}
          setSnoozeUntil={handleSnoozeUntilChange}
          snoozeNote={state.snoozeNote}
          setSnoozeNote={handleSnoozeNoteChange}
        />
      )}

      {!hasSnoozedService && (
        <JustificationContainer
          width="100%"
          margin={[hasAdditionalServices ? 's' : null, null, null, null]}
        >
          <Textarea
            label={text.additionalComments}
            name="additionalComments"
            value={state.additionalComments}
            onChange={value => handleAdditionalCommentsChange(value || '')}
            placeholder={text.additionalCommentsPlaceholder}
          />
        </JustificationContainer>
      )}
    </JustificationContainer>
  );
};

export default AppointmentHappened;
